import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { GridFilterItem } from '@mui/x-data-grid';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_CARD, RADIUS_COIN, SHADOW, YELLOW } from 'styles/tokens';

import SmartFilterRow from './SmartFilterRow';
import { filterRowsToGridItems, gridItemsToFilterRows } from './smartFilterMapping';
import type { FilterFieldDef, FilterRow, SmartFilterContext } from './smartFilterTypes';
import type { FilterOption } from './types';

interface SmartFilterModalProps {
  open: boolean;
  onClose: () => void;
  fieldDefs: FilterFieldDef[];
  dynamicOptions?: Record<string, FilterOption[]>;
  /** Currently applied GridFilterItems — parsed into rows when the modal opens. */
  initialItems: GridFilterItem[];
  /** Called with the new GridFilterItems when the user clicks Apply. */
  onApply: (items: GridFilterItem[]) => void;
  context: SmartFilterContext;
}

const makeRowId = () => crypto.randomUUID();

const SmartFilterModal: React.FC<SmartFilterModalProps> = ({
  open,
  onClose,
  fieldDefs,
  dynamicOptions,
  initialItems,
  onApply,
  context,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<FilterRow[]>([]);

  // Sync rows from initialItems each time modal opens
  const handleOpen = () => {
    const parsed = gridItemsToFilterRows(initialItems, fieldDefs);
    setRows(parsed.length > 0 ? parsed : []);
  };

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, { id: makeRowId(), fieldId: '', value: null }]);
  }, []);

  const handleRowChange = useCallback((id: string, updates: Partial<FilterRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  }, []);

  const handleRowRemove = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleClear = () => {
    setRows([]);
  };

  const handleApply = () => {
    const items = filterRowsToGridItems(rows, fieldDefs);
    onApply(items);
    onClose();
  };

  // A row is complete when it has a field selected AND a non-null value
  const isApplyDisabled = useMemo(() => {
    if (rows.length === 0) return true;
    return rows.some((r) => !r.fieldId || r.value === null || r.value === undefined || r.value === '');
  }, [rows]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        transition: { onEnter: handleOpen },
        paper: {
          sx: {
            borderRadius: isMobile ? 0 : RADIUS_CARD,
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: isMobile ? 'none' : SHADOW,
          },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pt: { xs: 2, sm: 3 },
          pb: 1.5,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: NAVY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TuneIcon sx={{ color: YELLOW, fontSize: '1.1rem' }} />
        </Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: NAVY, flex: 1 }}>
          Filtros avanzados
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: `${NAVY}80` }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ opacity: 0.12 }} />

      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.map((row) => (
            <SmartFilterRow
              key={row.id}
              row={row}
              fieldDefs={fieldDefs}
              dynamicOptions={dynamicOptions}
              context={context}
              onChange={handleRowChange}
              onRemove={handleRowRemove}
            />
          ))}

          {rows.length === 0 && (
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.9rem',
                color: `${NAVY}60`,
                textAlign: 'center',
                py: 3,
              }}
            >
              No hay filtros activos. Agrega uno para comenzar.
            </Typography>
          )}
        </Box>

        <Button
          onClick={handleAddRow}
          startIcon={<AddIcon />}
          sx={{
            mt: 2,
            textTransform: 'none',
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            fontSize: '0.85rem',
            color: NAVY,
            '&:hover': { backgroundColor: `${NAVY}08` },
          }}
        >
          Agregar filtro
        </Button>
      </DialogContent>

      <Divider sx={{ opacity: 0.12 }} />

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={handleClear}
          disabled={rows.length === 0}
          sx={{
            textTransform: 'none',
            fontFamily: 'Jost, sans-serif',
            fontWeight: 500,
            color: `${NAVY}80`,
            mr: 'auto',
          }}
        >
          Limpiar
        </Button>
        <Tooltip
          title={isApplyDisabled ? 'Agrega y configura al menos un filtro' : ''}
          placement="top"
          slotProps={{
            tooltip: {
              sx: { fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', backgroundColor: NAVY },
            },
          }}
        >
          <span>
            <Button
              onClick={handleApply}
              variant="contained"
              disabled={isApplyDisabled}
              sx={{
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                borderRadius: RADIUS_COIN,
                backgroundColor: NAVY,
                color: '#fff',
                px: 3,
                '&:hover': { backgroundColor: `${NAVY}dd` },
                '&.Mui-disabled': { backgroundColor: `${NAVY}14`, color: `${NAVY}60` },
              }}
            >
              Aplicar filtros
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SmartFilterModal;
